import { selectDimensions } from "../../../store/selectors/metaDataSelectors.ts";
import useRequestConfigBuilder from "./preview/hooks/useRequestConfigBuilder.ts";
import { useBiApiClientProvider } from "../../../contexts/BiApiClientProviderContext.ts";
import type { BiApiClient } from "../../../contexts/ApiClientProviderContext.ts";
import { getPivotExportWarning } from "../../common/export/exportWarnings.ts";
import ExcelReportExport from "../../common/export/ExcelReportExport.tsx";
import { Box, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import { selectRows } from "../../../store/selectors/fieldsStateSelectors.ts";
import PartContainer from "../../viewer/common/PartContainer.tsx";
import PreviewActions from "../../viewer/common/PreviewActions.tsx";
import EditingHistoryControls from "../common/EditingHistoryControls.tsx";
import type { EditorProps } from "../../viewer/common/Types.ts";
import BuildProgressWrapper from "../BuildProgressWrapper.tsx";
import ChangePreviewModeButton from "../common/ChangePreviewModeButton.tsx";
import BuilderMoreAction from "../common/fields/BuilderMoreAction.tsx";
import useTrackMeasuresListUpdate from "./hooks/useTrackMeasuresListUpdate.ts";
import PivotContainer from "./PivotContainer.tsx";
import EditPreviewComponent from "./preview/EditPreviewComponent.tsx";
import useGridStateBuilder from "./preview/hooks/useGridStateBuilder.ts";
import TableComponent from "./table/TableComponent.tsx";

export default function PivotEditor({ exportAllowed = true, ReportTypeSelector }: EditorProps) {
  useTrackMeasuresListUpdate();
  const { onRefreshGrid } = useBiCommonDataContext();
  const state = useGridStateBuilder();

  return (
    <PivotContainer>
      <Stack direction="row" sx={{ flex: 1 }}>
        <PartContainer
          sx={{ maxWidth: "24rem", minWidth: "24rem" }}
          caption={<Box sx={{ pl: "1.3rem" }}>Builder</Box>}
          actions={<BuilderMoreAction />}
        >
          <TableComponent ReportTypeSelector={ReportTypeSelector} pivotColumns={state.columns} />
        </PartContainer>
        <BuildProgressWrapper>
          <PartContainer
            sx={{ borderRight: "unset", p: "1.3rem", pt: 2 }}
            containerSx={{ position: "relative", gap: "1rem" }}
            caption="Preview"
            actions={
              <PreviewActions
                onRefresh={onRefreshGrid}
                ChangePreviewModeButton={<ChangePreviewModeButton />}
                EditingHistory={<EditingHistoryControls />}
              />
            }
          >
            <PivotEditorContainer exportAllowed={exportAllowed} gridState={state} />
          </PartContainer>
        </BuildProgressWrapper>
      </Stack>
    </PivotContainer>
  );
}

function PivotEditorContainer({
  exportAllowed = true,
  gridState,
}: {
  exportAllowed?: boolean;
  gridState: ReturnType<typeof useGridStateBuilder>;
}) {
  const rows = useSelector(selectRows);
  const { requestConfig } = useRequestConfigBuilder();
  const dimensions = useSelector(selectDimensions);
  const { requestBuildPivotExport } = useBiApiClientProvider<BiApiClient>();
  return (
    <>
      {exportAllowed && (
        <ExcelReportExport
          mode="build"
          requestConfig={requestConfig}
          dimensions={dimensions}
          requestExport={requestBuildPivotExport}
          warning={getPivotExportWarning(gridState, rows.length)}
        />
      )}
      <EditPreviewComponent gridState={gridState} />
    </>
  );
}
